import {Component, OnInit, OnDestroy} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { IsDirty } from '../../services/can-deactivate-guard/is-dirty.interface';
import { RequestTemplateService } from '../../services/request-template/request-template.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SelectQuestionnaireModalComponent } from '../../components/send-xref/components/select-questionnaire-modal/select-questionnaire-modal.component';
import { SelectBackgroundCheckModalComponent } from '../../components/send-xref/components/select-background-check-modal/select-background-check-modal.component';
import { BackgroundChecksService } from '../../services/background-checks/background-checks.service';
import { ConstantsService } from '../../services/constants/constants.service';
import { OrganisationService } from '../../services/organisation/organisation.service';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { QuestionnaireService } from '../../services/questionnaire/questionnaire.service';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {AlertBannerService, AlertType} from "../../lib/alert-banner";
import moment from "moment-timezone";
import {TrustMarketplaceStatusService} from "../../services/trust-marketplace-status/trust-marketplace-status.service";

@Component({
  selector: 'app-create-template',
  templateUrl: './create-template.component.pug',
  styleUrls: ['./create-template.component.scss'],
  standalone: false
})
export class CreateTemplateComponent implements OnInit, IsDirty, OnDestroy {
  templateName = '';
  private destroy$ = new Subject<void>()
  criteriaID = 0;
  isImmediatelyAvailable = true;
  selectedSurvey: any = null;
  questionnaireSelected: any = {};
  selectedBackgroundChecks: any[] = [];
  flowsSelected: any[] = [];
  bgCheckShortNames: any = [];
  selectedBackgroundChecksIds: any[] = [];
  backgroundChecksData: any = null;
  backgroundChecksList:any = [];
  org: any = null;
  defaultPayload: any = {};
  defaultNotes: any = {};
  private bsModalRef: any;
  
  // Edit mode properties
  isEditMode = false;
  isViewMode = false;
  templateId: number | null = null;
  templateData: any = null;
  flowSelected!: any;
  disableButton = false;
  invalidTitle = false;
  titlePattern=new RegExp(/[^a-zA-Z0-9 _\-():',.\/&]+/);
  wrongPattern = false;
  notesToCandidateValueError = false;
  notesToRefereeValueError = false;
  canManageTemplate = false;
  canManageBCTemplate = false;
  backgroundMember = false;
  canManageReferenceTemplate = false;
  TEMPLATE_REFERENCE_PERMISSION = $localize`Reference administrator permission is required to add a survey to a request template.`
  TEMPLATE_BC_PERMISSION = $localize`Background check administrator permission is required to add checks to a request template.`
  hasBackgroundCheckPermissions = false;
  tmpAdminStatus: number | undefined = 1;
  backgroundCheckTooltip = $localize`A Trust Marketplace integration is required. Contact the account owner or background check administrator to configure.`

  // Initial state tracking for isDirty comparison
  private initialTemplateName = '';
  private initialQuestionnaireSelected: any = {};
  private initialSelectedBackgroundChecks: any[] = [];
  private initialDefaultPayload: any = {};
  private initialIsImmediatelyAvailable = true;
  private initialCriteriaID = 0;
  private initialStateCaptured = false;
  // Track what has loaded in edit mode
  private questionnaireLoaded = false;
  private backgroundChecksLoaded = false;
  // Flag to indicate we're saving (so guard doesn't show modal)
  private isSaving = false;
  // Loading states
  nameCheckingLoading = false;
  saveLoading = false;

  constructor(
    private router: Router,
    private templateService: RequestTemplateService,
    private modalService: BsModalService,
    private bgCheckService: BackgroundChecksService,
    private organisationService: OrganisationService,
    private questionnaireService: QuestionnaireService,
    private alertList: AlertBannerService,
    private permissions: PermissionsService,
    private trustMarketplaceStatusService: TrustMarketplaceStatusService,
  ) {}

  ngOnInit(): void {
    if (!this.permissions.isFeatureActivated('ent-10605-request-templates')) {
        this.router.navigate(['/']);
        return;
    }
    this.canManageTemplate = this.permissions.hasPermission("can_manage_package")
    this.canManageBCTemplate = this.canManageTemplate || this.permissions.hasPermission("can_manage_backgroundcheck_package")
    this.backgroundMember = this.permissions.hasPermission("can_view_use_backgroundcheck_package")
    this.canManageReferenceTemplate = this.canManageTemplate ||  this.permissions.hasPermission("can_manage_reference_package")
    const navigation = this.router.getCurrentNavigation();
    let state: any = null;

    if (navigation?.extras?.state) {
      state = navigation.extras.state;
    }
    else {
      // Fallback: try to get from history state
      state = history.state;
    }

    if (state) {
        this.isViewMode = state?.isViewMode;
        this.templateData = state?.template;
        this.templateId = this.templateData?.id;
        this.templateName = this.templateData?.name || '';
    }
    if(!this.templateName){
        this.generateTemplateTitle();
    }

    // Load organisation data for request settings
    this.loadOrganisationData();

    // Load background checks data
    if(this.canManageTemplate || this.canManageBCTemplate || this.backgroundMember) {
        this.loadBackgroundChecksData();
    }
    this.fetchTrustMarketplaceStatus();
  }

  generateTemplateTitle(): void{
    const dateString = moment().startOf('day').format('ll');
    let name = $localize`Package ` + dateString;
    this.verifyTemplateTitle(name);
  }

  verifyTemplateTitle(name): void{
    this.templateName = name;
    this.nameCheckingLoading = true;
    // Call template api to check if name exists and get suggested name
    this.templateService.getNewTitle(name).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res && res.exists && res.suggested_name) {
          this.templateName = res.suggested_name;
        } else {
          this.templateName = name;
        }
        this.setValidations();
        this.nameCheckingLoading = false;
      },
      error: (error) => {
        // On error, use the original name
        console.error('Error checking template name:', error);
        this.templateName = name;
        this.setValidations();
        this.nameCheckingLoading = false;
      }
    });
  }

  setValidations(){
    this.disableButton = this.isSaveButtonDisabled();
  }

  isSaveButtonDisabled(): boolean {

    return( this.invalidTitle ||
      this.wrongPattern ||
      this.templateName.length === 0 ||
        (this.hasSelectedSurvey() && (this.notesToCandidateValueError || this.notesToRefereeValueError)) )
  }

  titleChange(title){
    this.invalidTitle=false;
    let patternMatch = this.titlePattern.test(title);
    this.wrongPattern = patternMatch;
    this.setValidations();
  }

  /**
   * Check if template has been used (active_used or inactive_used)
   */
  get isTemplateUsed(): boolean {
    if (!this.templateData) {
      return false;
    }
    const state = this.templateData.current_state;
    return state === 'active_used' || state === 'inactive_used';
  }

  /**
   * Load template data into the form
   */
  loadTemplateData(): void {
    if (!this.templateData) {
      return;
    }

    // Set template name and availability
    this.templateName = this.templateData?.name || '';
    this.isImmediatelyAvailable = this.templateData.is_active || false;

    // Load survey/questionnaire from config
    const referenceConfig = this.templateData.config?.find((c: any) => c.type === 'reference');
    if (referenceConfig && referenceConfig.package_id) {
      // Call getQuestionnaire to get the full questionnaire object
      this.questionnaireService.getQuestionnairePackage(referenceConfig.package_id.toString()).pipe(takeUntil(this.destroy$)).subscribe(
        (res) => {
          if (res && res.data) {
            // Map the response to the structure expected by questionnaireSelected
            // The response structure has res.data which contains the questionnaire details
            // We need to construct the object similar to what SelectQuestionnaireModalComponent returns
            const questionnaireData = res.data;

            // Build the questionnaire object structure
            this.questionnaireSelected = {
                ...questionnaireData,
              id: questionnaireData.id || referenceConfig.package_id,
              name: questionnaireData.name || referenceConfig.package_name,
              package_name: questionnaireData.name || referenceConfig.package_name,
              survey_type: questionnaireData.survey_type,
              config: questionnaireData.config,
              questionnaire: questionnaireData.questionnaire || {
                id: questionnaireData.id || referenceConfig.package_id,
                name: questionnaireData.name || referenceConfig.package_name,
                package_name: questionnaireData.name || referenceConfig.package_name,
                is_default: questionnaireData.questionnaire.is_default,
                third_party_questionnaire_id: questionnaireData.questionnaire.third_party_questionnaire_id
              }
            };

            // Set selectedSurvey to the same object
            this.selectedSurvey = this.questionnaireSelected;

            // Load request settings from additional_details if available, otherwise use questionnaire config
            if (referenceConfig.additional_details) {
              const details = referenceConfig.additional_details;
              // Convert API's lowercase gap_period to capitalized for dropdown: 'weeks' -> 'Weeks', 'days' -> 'Days'
              let gapPeriodDisplay = 'Weeks';
              if (details.gap_period === 'weeks') {
                gapPeriodDisplay = details.gap_count === 1 ? 'Week' : 'Weeks';
              } else if (details.gap_period === 'days') {
                gapPeriodDisplay = details.gap_count === 1 ? 'Day' : 'Days';
              }
              this.defaultPayload = {
                referenceCount: details.min_referee || 2,
                coverageCount: details.coverage_count || 2,
                timeGap: details.gap_count || 4,
                allowTimeGaps: details.gap_count != null,
                selectedTimeGapPeriod: gapPeriodDisplay,
                coverageCountAdded: !!details.coverage_count,
                noteToCandidate: details.note_to_candidate || '',
                noteToReferee: details.note_to_referee || ''
              };
              this.defaultNotes = {
                noteToCandidate: details.note_to_candidate || '',
                noteToReferee: details.note_to_referee || ''
              };
            } else if (questionnaireData.config) {
              // Use questionnaire config if additional_details not available
              this.defaultPayload = {
                referenceCount: questionnaireData.config.min_referee || 2,
                coverageCount: 2,
                timeGap: 4,
                selectedTimeGapPeriod: 'Weeks',
                coverageCountAdded: false,
                noteToCandidate: questionnaireData.config.notes?.note_to_candidate || '',
                noteToReferee: questionnaireData.config.notes?.note_to_referee || ''
              };
              this.defaultNotes = {
                noteToCandidate: questionnaireData.config.notes?.note_to_candidate || '',
                noteToReferee: questionnaireData.config.notes?.note_to_referee || ''
              };
            }

            // Set criteria_id if available
            if (referenceConfig.criteria_id) {
              this.criteriaID = referenceConfig.criteria_id;
            } else if (questionnaireData.questionnaire?.third_party_questionnaire_id?.criteria) {
              this.criteriaID = questionnaireData.questionnaire.third_party_questionnaire_id.criteria;
            }

            // Mark questionnaire as loaded and try to capture state
            // Use setTimeout to ensure all property assignments are complete
            this.questionnaireLoaded = true;
            setTimeout(() => {
              this.captureInitialState();
            }, 0);
          }
        },
        (error) => {
          console.error('Error loading questionnaire:', error);
          // Fallback to minimal structure if API call fails
          this.questionnaireSelected = {
            id: referenceConfig.package_id,
            name: referenceConfig.package_name,
            package_name: referenceConfig.package_name,
          };
          this.selectedSurvey = this.questionnaireSelected;

          // Load request settings from additional_details if available
          if (referenceConfig.additional_details) {
            const details = referenceConfig.additional_details;
            // Convert API's lowercase gap_period to capitalized for dropdown: 'weeks' -> 'Weeks', 'days' -> 'Days'
            let gapPeriodDisplay = 'Weeks';
            if (details.gap_period === 'weeks') {
              gapPeriodDisplay = details.gap_count === 1 ? 'Week' : 'Weeks';
            } else if (details.gap_period === 'days') {
              gapPeriodDisplay = details.gap_count === 1 ? 'Day' : 'Days';
            }
            this.defaultPayload = {
              referenceCount: details.min_referee || 2,
              coverageCount: details.coverage_count || 2,
              timeGap: details.gap_count || 4,
              allowTimeGaps: details.gap_count != null,
              selectedTimeGapPeriod: gapPeriodDisplay,
              coverageCountAdded: !!details.coverage_count,
              noteToCandidate: details.note_to_candidate || '',
              noteToReferee: details.note_to_referee || ''
            };
            this.defaultNotes = {
              noteToCandidate: details.note_to_candidate || '',
              noteToReferee: details.note_to_referee || ''
            };
          }

          if (referenceConfig.criteria_id) {
            this.criteriaID = referenceConfig.criteria_id;
          }

          // Mark questionnaire as loaded (even if error) and try to capture state
          this.questionnaireLoaded = true;
          setTimeout(() => {
            this.captureInitialState();
          }, 0);
        }
      );
    } else {
      // No reference config - mark as loaded so we don't wait for it
      this.questionnaireLoaded = true;
      // Try to capture state (will wait for background checks if in edit mode)
      setTimeout(() => {
        this.captureInitialState();
      }, 0);
    }
  }

  /**
   * Load background checks from API and map template's background checks to the API response
   * Handles format with partner_check_shortnames and partners arrays
   */
  private loadAndMapBackgroundChecks(backgroundConfig: any): void {
      // Call the API to get all background checks (pass null/undefined to get all)
      this.bgCheckService.getBackgroundChecks();
      this.bgCheckService.bgChecks.pipe(takeUntil(this.destroy$)).subscribe(checks => {
          if(checks && typeof checks === 'object' && !Array.isArray(checks) && Object.keys(checks).length > 0){
              this.backgroundChecksData = checks;
              this.backgroundChecksList = checks['categorizedChecks'];
              if(backgroundConfig){
                  this.mapBackgroundChecksFromTemplate(backgroundConfig, checks);
              } else {
                  // No background config - mark as loaded and try to capture state
                  this.backgroundChecksLoaded = true;
                  setTimeout(() => {
                    this.captureInitialState();
                  }, 0);
              }

          } else {
              // No checks data - mark as loaded and try to capture state
              this.backgroundChecksLoaded = true;
              setTimeout(() => {
                this.captureInitialState();
              }, 0);
          }
      });
  }

  /**
   * Map background check shortnames from template config to actual background check objects from API
   * Matches the structure returned by SelectBackgroundCheckModalComponent
   */
  private mapBackgroundChecksFromTemplate(backgroundConfig: any, apiBackgroundChecks: any): void {

    const partnerCheckShortnames = backgroundConfig.partner_checks || [];
    const partners = backgroundConfig.partners || [];

    // MobileID flow shortnames that should be mapped to mobile_id parent
    const mobileIdFlows = [
      ConstantsService.ID_ONE_DOC_SHORT_NAME,
      ConstantsService.ID_TWO_DOCS_SHORT_NAME,
      ConstantsService.ID_ONE_DOC_DVS_SHORT_NAME,
      ConstantsService.ID_TWO_DOCS_DVS_SHORT_NAME
    ];

    // Find matching background checks by short_name from API response
    this.selectedBackgroundChecks = [];
    this.flowsSelected = [];

    partnerCheckShortnames.forEach((shortName: any, index: number) => {
      // Check if this is a mobile_id flow (like id_doc_one, id_doc_two, etc.)
      const isMobileIdFlow = mobileIdFlows.includes(shortName.short_name);

      let check: any = null;
      let flow: any = null;
      let parentCheck: any = null;

      if (isMobileIdFlow) {
        // Find the mobile_id parent check
        parentCheck = apiBackgroundChecks.categorizedChecks.find((bgCheck: any) => bgCheck.short_name === 'mobile_id');

        // Find the specific flow within mobile_id's flow_names or config.flows
        if (parentCheck) {
          if (parentCheck.flow_names && Array.isArray(parentCheck.flow_names)) {
            flow = parentCheck.flow_names.find((f: any) => f.short_name === shortName.short_name);
          } else if (parentCheck.config?.flows && Array.isArray(parentCheck.config.flows)) {
            flow = parentCheck.config.flows.find((f: any) => f.short_name === shortName.short_name);
          }
          check = parentCheck; // Use parent check
        }

        // If not found, check if the shortName itself is a check
        if (!check) {
          check = apiBackgroundChecks.categorizedChecks.find((bgCheck: any) => bgCheck.short_name === shortName.short_name);
        }
      } else {
        // Regular check - first try to find by short_name
        check = apiBackgroundChecks.categorizedChecks.find((bgCheck: any) => bgCheck.short_name === shortName.short_name);

        // If not found, check if shortName is a flow shortname within any check's flows
        if (!check) {
          for (const bgCheck of apiBackgroundChecks) {
            // Check flow_names array
            if (bgCheck.flow_names && Array.isArray(bgCheck.flow_names)) {
              flow = bgCheck.flow_names.find((f: any) => f.short_name === shortName);
              if (flow) {
                check = bgCheck;
                break;
              }
            }
            // Check config.flows array
            if (bgCheck.config?.flows && Array.isArray(bgCheck.config.flows)) {
              flow = bgCheck.config.flows.find((f: any) => f.short_name === shortName);
              if (flow) {
                check = bgCheck;
                break;
              }
            }
          }
        } else {
          // Check found, see if it has flows and if shortName matches a flow
          if (check.flow_names && Array.isArray(check.flow_names)) {
            flow = check.flow_names.find((f: any) => f.short_name === shortName.flow_name);
          } else if (check.config?.flows && Array.isArray(check.config.flows)) {
            flow = check.config.flows.find((f: any) => f.short_name === shortName.flow_name);
          }
        }
      }

      if (check) {
        // Get partner/provider info
        const partnerShortName = partners[index] || check.provider_name?.toLowerCase() || '';

        // Create object matching SelectBackgroundCheckModalComponent structure
        const mappedCheck: any = {
          uid: check.uid || check.id || '',
          short_name: check.short_name,
          name: check.name || '',
          description: check.description || null,
          provider: check.provider || null,
          provider_name: check.provider_name || '',
          config: check.config || {}
        };

        // Add flow_names if available (from check.flow_names or check.config.flows)
        if (check.flow_names && Array.isArray(check.flow_names)) {
          mappedCheck.flow_names = check.flow_names;
        } else if (check.config?.flows && Array.isArray(check.config.flows)) {
          mappedCheck.flow_names = check.config.flows;
        }

        this.selectedBackgroundChecks.push(mappedCheck);

        // Add flow to flowsSelected if a specific flow was matched
        if (flow) {
          const flowObj = {
            id: flow.uid || flow.id || '',
            name: flow.name || '',
            short_name: flow.short_name,
            parent_short: check.short_name
          };
          this.flowsSelected.push(flowObj);
        }
      }
    });

    // After mapping, update bgCheckShortNames and selectedBackgroundChecksIds
    // This matches what happens when the modal returns data (lines 441-454)
    this.bgCheckShortNames = this.selectedBackgroundChecks.map(check => {
      if (check.short_name === 'mobile_id') {
        const matchingFlow = this.flowsSelected.find(flow => flow.parent_short === check.short_name);
        return matchingFlow ? matchingFlow.short_name : check.short_name;
      }
      return check.short_name;
    });

    this.selectedBackgroundChecksIds = this.selectedBackgroundChecks.map(check => {
      if (check.short_name === 'mobile_id' || check.flow_names?.length) {
        const matchingFlow = this.flowsSelected.find(flow => flow.parent_short === check.short_name);
        return matchingFlow ? matchingFlow.id : check.uid;
      }
      return check.uid;
    });

    // Mark background checks as loaded and capture initial state
    // This ensures all data (questionnaire + background checks) is loaded before capturing
    // Use a small delay to ensure all property assignments are complete
    this.backgroundChecksLoaded = true;
    setTimeout(() => {
      this.captureInitialState();
    }, 100); // Small delay to ensure all async operations complete
  }

  loadOrganisationData(): void {
    this.organisationService.getOrganisation().pipe(takeUntil(this.destroy$)).subscribe({
      next: (orgDetails: any) => {
        this.org = orgDetails;
        // Only set defaults if not in edit mode (template data will set them)
        this.defaultPayload = {
            referenceCount: 2,
            coverageCount: 2,
            timeGap: 4,
            selectedTimeGapPeriod: 'Weeks',
            coverageCountAdded: false,
            noteToCandidate: this.org?.notes?.note_to_candidate || '',
            noteToReferee: this.org?.notes?.note_to_referee || ''
          };
        this.defaultNotes = {
          noteToCandidate: orgDetails?.notes?.note_to_candidate || '',
          noteToReferee: orgDetails?.notes?.note_to_referee || ''
        };
        // Check if we're in edit mode
        const navigation = this.router.getCurrentNavigation();
        let state: any = null;

        if (navigation?.extras?.state) {
          state = navigation.extras.state;
        } else {
          // Fallback: try to get from history state
          state = history.state;
        }

        if (state) {
          if(state?.isEditMode){
              this.isEditMode = true;
          }
          // Check for edit/view mode
          if ((state?.isEditMode || state?.isViewMode) && state?.template) {
            this.templateData = state['template'];
            this.templateId = this.templateData.id;
            this.loadTemplateData();
            // Don't capture state here - wait for template data and background checks to load
            // State will be captured in mapBackgroundChecksFromTemplate or loadAndMapBackgroundChecks
          } else {
            // Not in edit mode - create mode - capture initial state after org data is loaded
            // Use setTimeout to ensure this runs after all initialization
            setTimeout(() => {
              if (!this.initialStateCaptured) {
                this.captureInitialState();
              }
            }, 0);
          }
        } else {
          // No state - create mode - capture initial state after org data is loaded
          setTimeout(() => {
            if (!this.initialStateCaptured) {
              this.captureInitialState();
            }
          }, 0);
        }


      },
      error: (error) => {
        console.error('Error loading organisation data:', error);
      }
    });
  }

  requestSettingsError(error: any): void {
    console.log('Request settings error:', error);
  }

  requestSettingsData(payload: any): void {

    if (payload) {
      // this.updatedRequestSettings = payload
      this.defaultPayload = {
        ...payload,
      }
    }
    this.notesToCandidateValueError = !this.defaultPayload.noteToCandidate || this.defaultPayload.noteToCandidate.trim() === '';
    this.notesToRefereeValueError = !this.defaultPayload.noteToReferee || this.defaultPayload.noteToReferee.trim() === '';
    this.setValidations();
  }

  loadBackgroundChecksData(): void {

    const backgroundConfig = this.templateData?.config?.find((c: any) => c.type === 'background');
    if (backgroundConfig) {
      // Check for either format
      if (backgroundConfig.partner_checks && Array.isArray(backgroundConfig.partner_checks)) {
        // Format 2: Has partner_checks array with full objects
        this.loadAndMapBackgroundChecks(backgroundConfig);
      } else {
        // Background config exists but no partner_checks - mark as loaded and capture state
        this.backgroundChecksLoaded = true;
        setTimeout(() => {
          this.captureInitialState();
        }, 0);
      }
    }
    else{
        this.loadAndMapBackgroundChecks(undefined);
    }

  }

  onClose(): void {
    this.router.navigate(['/account'], { queryParams: { tab: 'requestTemplates' } });
  }

  onReplaceSurvey(): void {
    // Disable if template is used
    if (this.isTemplateUsed) {
      return;
    }

    const surveyType = ConstantsService.REFERENCE_CHECK;
    const initialState = {
      "questionnaireList": [], // The modal component will load the list internally
      surveyType: surveyType
    };

    this.bsModalRef = this.modalService.show(SelectQuestionnaireModalComponent, {
      class: 'modal-dialog-centered modal-dialog-scrollable modal-lg',
      initialState
    });

    this.bsModalRef.content.nextClick.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res && res.questionnaire) {
        // Update request settings based on selected survey first
        this.updateQuestionnaireSelected(res);
        // Store in the same format as send-xref uses (after updateQuestionnaireSelected sets it)
        this.selectedSurvey = this.questionnaireSelected;
      }
    });
  }

  updateQuestionnaireSelected(res: any): void {
    // Set questionnaireSelected to the full questionnaire object from the response
    this.questionnaireSelected = res['questionnaire'];

    // Ensure survey_type is set if not present (for reference surveys)
    if (!this.questionnaireSelected?.survey_type && res['surveyType']) {
      const surveyTypeKey = res['surveyType'] === 'reference' ? 'reference' : res['surveyType'];
      this.questionnaireSelected.survey_type = QuestionnaireService.survey_type_mapping[surveyTypeKey]?.toString();
    }

    const config = this.questionnaireSelected?.config;
    const default_questionnaire = this.questionnaireSelected?.questionnaire?.is_default;

    // Update reference count from survey config
    if (config?.min_referee !== undefined) {
        this.defaultPayload.referenceCount = config.min_referee;
    }

    // Update notes based on whether it's a default questionnaire or has custom config
    if (default_questionnaire) {
        // Use organization default notes
        this.defaultPayload.noteToCandidate = this.org?.notes?.note_to_candidate || '';
        this.defaultPayload.noteToReferee = this.org?.notes?.note_to_referee || '';
        this.defaultNotes.noteToCandidate = this.org?.notes?.note_to_candidate || '';
        this.defaultNotes.noteToReferee = this.org?.notes?.note_to_referee || '';
      } else {
        // Use survey-specific notes if available, otherwise fall back to org defaults
        this.defaultPayload.noteToCandidate = config?.notes?.note_to_candidate || this.org?.notes?.note_to_candidate || '';
        this.defaultPayload.noteToReferee = config?.notes?.note_to_referee || this.org?.notes?.note_to_referee || '';
        this.defaultNotes.noteToCandidate = config?.notes?.note_to_candidate || this.org?.notes?.note_to_candidate || '';
        this.defaultNotes.noteToReferee = config?.notes?.note_to_referee || this.org?.notes?.note_to_referee || '';
    }
    this.criteriaID = this.questionnaireSelected?.['questionnaire']?.['third_party_questionnaire_id']?.['criteria'] || 0;
  }

  onAddChecks(): void {
    // Disable if template is used
    if (this.isTemplateUsed) {
      return;
    }

    this.bsModalRef = this.modalService.show(SelectBackgroundCheckModalComponent, {
      class: 'modal-dialog-centered xl-popup',
      initialState: {
        backgroundChecks: this.selectedBackgroundChecks,
        backgroundChecksData: this.backgroundChecksData,
        flows: this.flowsSelected
      }
    });

    this.bsModalRef.content?.nextClick.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res && res.backgroundCheck) {

          // Update selected background checks
          this.selectedBackgroundChecks = res?.backgroundCheck;
          this.flowsSelected = res?.flows;
          // this.disableRecipientListButtons = this.selectedBackgroundChecks.length === 0;

          this.bgCheckShortNames = this.selectedBackgroundChecks.map(check => {
              if (check.short_name === 'mobile_id') {
                const matchingFlow = this.flowsSelected.find(flow => flow.parent_short === check.short_name);
                return matchingFlow ? matchingFlow.short_name : check.short_name;
              }
              return check.short_name;
          });
          this.selectedBackgroundChecksIds = this.selectedBackgroundChecks.map(check => {
              if (check.short_name === 'mobile_id' || check.flow_names?.length) {
                const matchingFlow = this.flowsSelected.find(flow => flow.parent_short === check.short_name);
                return matchingFlow ? matchingFlow.id : check.uid;
              }
              return check.uid;
          });
          this.setValidations();
      }
    });
  }

  onDeleteSurvey(): void {
    this.selectedSurvey = null;
    this.questionnaireSelected = {};
    // Reset request settings to organization defaults when survey is deleted
    this.resetRequestSettingsToDefaults();
  }

  deleteSelection(): void {
    this.onDeleteSurvey();
  }

  resetRequestSettingsToDefaults(): void {
    if (this.org) {
      this.defaultPayload = {
        referenceCount: 2,
        coverageCount: 2,
        timeGap: 4,
        selectedTimeGapPeriod: 'Weeks',
        coverageCountAdded: false,
        noteToCandidate: this.org?.notes?.note_to_candidate || '',
        noteToReferee: this.org?.notes?.note_to_referee || ''
      };
      this.defaultNotes = {
        noteToCandidate: this.org?.notes?.note_to_candidate || '',
        noteToReferee: this.org?.notes?.note_to_referee || ''
      };
    }
  }

  onDeleteCheck(check: any): void {
    const updateCheckItems = () => {
      if(check.short_name === 'mobile_id')
        this.flowSelected = {}
      this.selectedBackgroundChecks = this.selectedBackgroundChecks.filter(bgCheck => bgCheck.uid !== check.uid)
      this.flowsSelected = this.flowsSelected.filter(bgCheck => bgCheck.parent_short !== check.short_name)
      this.selectedBackgroundChecksIds = this.selectedBackgroundChecks.map(check => check.uid);
      this.bgCheckShortNames = this.selectedBackgroundChecks.map(check => check.short_name);

    }
    updateCheckItems()
  }

  deleteBackgroundCheckSelection(): void {
    this.selectedBackgroundChecks = [];
    this.flowsSelected = [];
    this.bgCheckShortNames = [];
    this.selectedBackgroundChecksIds = [];
  }

  removeSelectedCheck(check: any): void {
    this.onDeleteCheck(check);
  }

  onCancel(): void {
    this.onClose();
  }

  onCreateTemplate(): void {
    const templateData: any = {
      name: this.templateName,
      description: '', // Description can be added later if needed
      config: [] as any[],
      is_active: this.isImmediatelyAvailable,
      is_api_accessible: this.isImmediatelyAvailable
    };

    // Add reference config if survey is selected
    if (this.questionnaireSelected && Object.keys(this.questionnaireSelected).length > 0) {
      const referenceConfig: any = {
        type: 'reference',
        package_id: this.questionnaireSelected.id,
        package_name: this.questionnaireSelected.name
      };

      // Add criteria_id if available
      if (this.criteriaID) {
        referenceConfig.criteria_id = this.criteriaID;
      }

      // Add request settings to additional_details
      const additionalDetails: any = {};

      if (this.defaultPayload) {
        additionalDetails.min_referee = this.defaultPayload.referenceCount || 2;
        if (this.defaultPayload.coverageCountAdded){
            additionalDetails.coverage_count = this.defaultPayload.coverageCount
        }
        if(this.defaultPayload.allowTimeGaps){
            additionalDetails.gap_count = this.defaultPayload.timeGap;
            // Convert to lowercase for backend: 'Week'/'Weeks' -> 'weeks', 'Day'/'Days' -> 'days'
            const period = this.defaultPayload.selectedTimeGapPeriod?.toLowerCase() || '';
            additionalDetails.gap_period = period.startsWith('week') ? 'weeks' : period.startsWith('day') ? 'days' : '';
        }
        additionalDetails.note_to_candidate = this.defaultPayload.noteToCandidate || this.defaultNotes.noteToCandidate || '';
        additionalDetails.note_to_referee = this.defaultPayload.noteToReferee || this.defaultNotes.noteToReferee || '';
      }
      
      referenceConfig.additional_details = additionalDetails;
      templateData.config.push(referenceConfig);
    }

    // Add background config if background checks are selected
    if (this.selectedBackgroundChecks.length > 0) {
      const partnerChecks = this.selectedBackgroundChecks.map(check => {
        // Find matching flow if this check has flows
        let flowName = '';
        if (check.short_name === 'mobile_id' || check.flow_names?.length) {
          const matchingFlow = this.flowsSelected.find(flow => flow.parent_short === check.short_name);
          if (matchingFlow) {
            flowName = matchingFlow.short_name;
          }
        }
        
        const partnerCheck: any = {
          short_name: check.short_name,
          name: check.name,
          partner_short_name: check.partner_short_name || check.provider_name?.toLowerCase() || ''
        };
        
        // Add flow_name if available
        if (flowName) {
          partnerCheck.flow_name = flowName;
        }
        
        return partnerCheck;
      });
      
      templateData.config.push({
        type: 'background',
        partner_checks: partnerChecks,
        additional_details: {}
      });
    }

    // Set flag to prevent warning modal when navigating after save
    this.isSaving = true;
    this.saveLoading = true;

    if (this.isEditMode && this.templateId) {
      // Update existing template
      this.templateService.updateTemplate(this.templateId, templateData).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response) => {
          this.saveLoading = false;
          this.alertList.append([{
              type: AlertType.SUCCESS,
              message: $localize`Success! The package, ${templateData.name}, has been saved.`,
              keepOnRouteChange: true
            }])
          this.router.navigate(['/account'], { queryParams: { tab: 'requestTemplates' } });
        },
        error: (error) => {
          // Reset flag on error so modal can show if user tries to navigate away
          this.isSaving = false;
          this.saveLoading = false;
          const message: string = error?.error?.message || '';
          if(error.status==400 && message.toLowerCase().includes('already exists')){
              this.invalidTitle = true
              this.disableButton = true
          }
          else {
              this.alertList.append([{
                  type: AlertType.DANGER,
                  message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
                  keepOnRouteChange: true
              }]);
              this.router.navigate(['/account'], {queryParams: {tab: 'requestTemplates'}});
          }
        }
      });
    } else {
      // Create new template
      this.templateService.createTemplate(templateData).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response) => {
          this.saveLoading = false;
          this.alertList.append([{
              type: AlertType.SUCCESS,
              message: $localize`Success! The package, ${templateData.name}, has been created.`,
              keepOnRouteChange: true
            }])
          this.router.navigate(['/account'], { queryParams: { tab: 'requestTemplates' } });
        },
        error: (error) => {
          // Reset flag on error so modal can show if user tries to navigate away
          this.isSaving = false;
          this.saveLoading = false;
          const message: string = error?.error?.message || '';
          if(error.status==400 && message.toLowerCase().includes('already exists')){
              this.invalidTitle = true
              this.disableButton = true
          }
          else {
              this.alertList.append([{
                  type: AlertType.DANGER,
                  message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
                  keepOnRouteChange: true
              }]);
              this.router.navigate(['/account'], {queryParams: {tab: 'requestTemplates'}});
          }
        }
      });
    }
  }

  /**
   * Capture initial state after all data is loaded
   * This should be called after template data is loaded (for edit mode) or after defaults are set (for create mode)
   */
  private captureInitialState(): void {
    if (this.initialStateCaptured) {
      return; // Only capture once
    }

    // In edit/view mode, wait for both questionnaire and background checks API calls to complete
    if (this.isEditMode || this.isViewMode) {
      // Check if we need to wait for more data
      const hasReferenceConfig = this.templateData?.config?.find((c: any) => c.type === 'reference');
      
      // Always wait for questionnaire if there's a reference config
      if (hasReferenceConfig && !this.questionnaireLoaded) {
        return; // Wait for questionnaire to load
      }
      
      // Always wait for background checks API to complete (even if no background config in template)
      // This ensures selectedBackgroundChecks is properly initialized (empty array vs undefined)
      if (!this.backgroundChecksLoaded) {
        return; // Wait for background checks API to load
      }
    }

    this.initialTemplateName = this.templateName || '';
    // Normalize questionnaire when capturing to ensure consistent comparison
    this.initialQuestionnaireSelected = this.deepClone(this.normalizeQuestionnaire(this.questionnaireSelected));
    // Normalize background checks when capturing to ensure consistent comparison
    this.initialSelectedBackgroundChecks = (this.selectedBackgroundChecks || []).map(check => 
      this.deepClone(this.normalizeBackgroundCheck(check))
    );
    this.initialDefaultPayload = this.deepClone(this.defaultPayload);
    this.initialIsImmediatelyAvailable = this.isImmediatelyAvailable;
    this.initialCriteriaID = this.criteriaID;
    this.initialStateCaptured = true;
  }

  /**
   * Deep clone helper for object comparison
   */
  private deepClone(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map(item => this.deepClone(item));
    }
    if (typeof obj === 'object') {
      const cloned: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          cloned[key] = this.deepClone(obj[key]);
        }
      }
      return cloned;
    }
    return obj;
  }

  /**
   * Normalize background check object for comparison (only compare relevant fields)
   */
  private normalizeBackgroundCheck(check: any): any {
    if (!check) return check;
    return {
      uid: check.uid || check.id || '',
      short_name: check.short_name || '',
      name: check.name || '',
      // Only include essential fields for comparison
    };
  }

  /**
   * Normalize questionnaire object for comparison (only compare relevant fields)
   */
  private normalizeQuestionnaire(questionnaire: any): any {
    if (!questionnaire || Object.keys(questionnaire).length === 0) {
      return {};
    }
    return {
      id: questionnaire.id || '',
      name: questionnaire.name || '',
      package_name: questionnaire.package_name || '',
      survey_type: questionnaire.survey_type || '',
      // Only include essential fields for comparison
    };
  }

  /**
   * Compare two objects/arrays for equality
   */
  private isEqual(a: any, b: any): boolean {
    if (a === b) {
      return true;
    }
    if (a === null || a === undefined || b === null || b === undefined) {
      return a === b;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }
      // For arrays, compare each element
      for (let i = 0; i < a.length; i++) {
        if (!this.isEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a).sort();
      const keysB = Object.keys(b).sort();
      if (keysA.length !== keysB.length) {
        return false;
      }
      for (const key of keysA) {
        if (!this.isEqual(a[key], b[key])) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  // IsDirty interface implementation
  isDirty(): boolean {
    // Never show modal in view mode
    if (this.isViewMode) {
      return false;
    }

    // Don't show modal when saving (user intentionally navigating away after save)
    if (this.isSaving) {
      return false;
    }

    // If initial state hasn't been captured yet, don't show modal
    // This prevents showing modal during initial load
    if (!this.initialStateCaptured) {
      return false;
    }

    // Compare template name
    if (this.templateName !== this.initialTemplateName) {
      return true;
    }

    // Compare questionnaire/survey selection
    const normalizedCurrentQuestionnaire = this.normalizeQuestionnaire(this.questionnaireSelected);
    const normalizedInitialQuestionnaire = this.normalizeQuestionnaire(this.initialQuestionnaireSelected);
    if (!this.isEqual(normalizedCurrentQuestionnaire, normalizedInitialQuestionnaire)) {
      return true;
    }

    // Compare background checks - normalize to only compare essential fields
    const normalizedCurrentChecks = (this.selectedBackgroundChecks || []).map(check => this.normalizeBackgroundCheck(check));
    const normalizedInitialChecks = (this.initialSelectedBackgroundChecks || []).map(check => this.normalizeBackgroundCheck(check));
    if (!this.isEqual(normalizedCurrentChecks, normalizedInitialChecks)) {
      return true;
    }

    // Compare request settings (defaultPayload)
    // Only check meaningful fields that can be changed
    const payloadFieldsToCheck = [
      'referenceCount',
      'coverageCount',
      'coverageCountAdded',
      'timeGap',
      'allowTimeGaps',
      'selectedTimeGapPeriod',
      'noteToCandidate',
      'noteToReferee'
    ];
    for (const field of payloadFieldsToCheck) {
      if (this.defaultPayload[field] !== this.initialDefaultPayload[field]) {
        return true;
      }
    }

    // Compare immediate availability
    if (this.isImmediatelyAvailable !== this.initialIsImmediatelyAvailable) {
      return true;
    }

    // Compare criteria ID
    if (this.criteriaID !== this.initialCriteriaID) {
      return true;
    }

    return false;
  }

  hasSelectedSurvey(): boolean {
    return this.questionnaireSelected && Object.keys(this.questionnaireSelected).length > 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLabel(available){
      return available ? $localize`Active` : $localize`Inactive`;
  }

  fetchTrustMarketplaceStatus() {
    this.trustMarketplaceStatusService.trustStatus$.pipe(takeUntil(this.destroy$)).subscribe(status_code => {
      this.hasBackgroundCheckPermissions = status_code === 3;
      this.backgroundCheckTooltip = this.hasBackgroundCheckPermissions ? '' : this.backgroundCheckTooltip

      }, error => {
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
          keepOnRouteChange: true
        }]);
      }
    );
    this.trustMarketplaceStatusService.trustAdminStatus.pipe(takeUntil(this.destroy$)).subscribe(
      res => {
        if (res != undefined) {
          this.tmpAdminStatus = res;
        }
      }, error => {
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
          keepOnRouteChange: true
        }]);
      }
    );
  }
  getBackgroundCheckTooltip() {
    if (!this.hasBackgroundCheckPermissions) {
      return this.backgroundCheckTooltip;
    } else if (this.hasBackgroundCheckPermissions && !this.canManageBCTemplate) {
      return this.TEMPLATE_BC_PERMISSION
    } else if (this.hasBackgroundCheckPermissions && this.canManageBCTemplate) {
      return '';
    }
    return ''
  }

}

